import { connect } from "react-redux";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import * as creators from "../actions/creators";
import * as api from "../actions/api";
import Card from "./Card";
import DisplayRow from "./DisplayRow";
import ApiKeyInlineCopy from "./ApiKeyInlineCopy";
import TextInputWrap from "./TextInputWrap";
import LinkInput from "./UrlInputWrap";
import Toggle from "../../shared/v2/Toggle";
import Dropdown from "../../shared/v2/Dropdown";
import { useCurrentSuperUser, useCurrentUser, useTheme } from "../../reducers/layoutReducer/selectors";

const DATE_FORMAT = "MM/DD/YYYY";

const isTeamNameEditable = (isSuperUser, isAdmin, isPlace) =>
  (isSuperUser && isPlace) || (isAdmin && !isPlace);

const TeamCard = ({ setValue, model, timezones, saveValue }) => {
  const { isAdmin, isOwner, isPlace } = useCurrentUser().account;

  const SECTIONS = [
    {
      title: "Team Name",
      key: "team_name",
      comp: TextInputWrap,
      tooltip: "Reach out to support@place.com if you plan to update your team name",
    },
    {
      title: "Brokerage Name",
      key: "broker_name",
      editable: true,
      comp: TextInputWrap,
    },
    { title: "Team Type", key: "team_type", editable: false, placeOnly: true, value: "PLACE team" },
    {
      title: "Website",
      key: "website",
      editable: true,
      comp: LinkInput,
      placeholder: "Paste website URL",
    },
    {
      title: "Compliance License #",
      key: "compliance_license_number",
      editable: true,
      comp: TextInputWrap,
    },
    {
      title: "Partnership Effective Date",
      key: "partnership_effective",
      editable: false,
      comp: TextInputWrap,
      placeOnly: true,
      displayValue: (value) => (value > "" ? dayjs(value).format(DATE_FORMAT) : "—"),
    },
    {
      title: "Current Contract Effective Date",
      key: "current_contract_effective",
      editable: false,
      placeOnly: true,
      displayValue: (value) => (value > "" ? dayjs(value).format(DATE_FORMAT) : "—"),
    },
    {
      title: "Go-Live Date",
      tooltip: "Denotes the date where the team’s revenue and expenses become shared with PLACE.",
      key: "go_live",
      editable: false,
      placeOnly: true,
      displayValue: (value) => (value > "" ? dayjs(value).format(DATE_FORMAT) : "—"),
    },
    {
      title: "Visible in Partner Search",
      tooltip:
        "If disabled, your team won't appear in search results for the PLACE network's Partner search feature",
      key: "partner_search_enabled",
      editable: true,
      comp: ({ value, onChange }) => (
        <Toggle size="small" checked={value} onChange={(e) => onChange(e.target.checked)} />
      ),
      placeOnly: true,
      alwaysEdit: true,
    },
    {
      title: "Account Time Zone",
      key: "time_zone",
      editable: true,
      isDD: true,
      comp: ({ value, onChange, onBlur, onKeyPress }) => (
        <Dropdown
          isSearchable
          defaultMenuIsOpen
          autoFocus
          containerClassName="tw-w-full tw-flex-1 tw-min-w-[200px]"
          placeholder="Select a timezone"
          value={value ? timezones.find((el) => el.value === value) : null}
          onBlur={onBlur}
          options={timezones}
          onKeyDown={onKeyPress}
          onChange={(e) => onChange(e?.value)}
          menuShouldComeToFront
        />
      ),
    },
    {
      title: "API Key",
      key: "api_key",
      editable: false,
      value: isOwner ? null : "Contact your Account Owner for assistance",
      displayValue: isOwner ? (value) => <ApiKeyInlineCopy apiKey={value} /> : null,
    },
  ];
  const isSuperUser = useCurrentSuperUser();

  return (
    <Card title="Team">
      {SECTIONS.map((sec) =>
        !isPlace && sec.placeOnly ? null : (
          <DisplayRow
            key={sec.key}
            title={sec.title}
            tooltip={(() => {
              if (sec.key === "team_name") {
                return !isTeamNameEditable(isSuperUser, isAdmin, isPlace) && sec.tooltip;
              }

              return sec.tooltip;
            })()}
            tooltipIconClassName={sec.key === "team_name" ? "tw-text-gray-75" : ""}
            editable={
              sec.key === "team_name" ? isTeamNameEditable(isSuperUser, isAdmin, isPlace) : sec.editable
            }
            alwaysEdit={sec.alwaysEdit}
            displayValue={sec.displayValue}
            value={sec.value ? sec.value : model[sec.key]}
            onChange={(val) => setValue(sec.key, val)}
            onSave={(val) => saveValue(sec.key, val)}
            Comp={sec.comp}
            isDD={sec.isDD}
          />
        ),
      )}
    </Card>
  );
};

TeamCard.propTypes = {
  model: PropTypes.shape({}).isRequired,
  setValue: PropTypes.func.isRequired,
  saveValue: PropTypes.func.isRequired,
  timezones: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const mapStateToProps = (state) => ({
  model: state.businessSettings.model,
  timezones: state.businessSettings.timezones,
});
export default connect(mapStateToProps, { ...creators, ...api })(TeamCard);
